import React, { useCallback } from "react";
import { useAppDispatch } from "hooks/redux";
import Portal from "elements/Portal";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { TWebsiteBuilderSection } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { WEBSITE_BUILDER_PANEL_ID } from "pages/secure/facility/websiteBuilder/editor/shell/panel/WebsiteBuilderPanel";
import { SectionSettings } from "./settings/SectionSettings";

export interface IUseSectionViewSettingsArgs {
  node: TWebsiteBuilderSection;
  selected: boolean;
  settingsActive: boolean;
}

interface IUseSectionViewSettings {
  viewSettings: JSX.Element;
  handleViewSettingsClick: () => void;
}

export function useSectionViewSettings(args: IUseSectionViewSettingsArgs): IUseSectionViewSettings {
  const dispatch = useAppDispatch();

  const closeSettings = useCallback(() => {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: "section",
          editingMode: { type: "none" },
        },
      }),
    );
  }, []);

  const handleViewSettingsClick = useCallback(() => {
    if (args.node == null || !args.selected) {
      return;
    }

    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: "section",
          editingMode: { type: args.settingsActive ? "none" : "section-settings" },
        },
      }),
    );
  }, [args.node, args.selected, args.settingsActive]);

  function renderViewSettings() {
    if (args.node == null || !args.selected || !args.settingsActive) {
      return null;
    }

    return (
      <Portal parentId={WEBSITE_BUILDER_PANEL_ID}>
        <SectionSettings key={args.node.id} node={args.node} closeSettings={closeSettings} />
      </Portal>
    );
  }

  return {
    viewSettings: renderViewSettings(),
    handleViewSettingsClick,
  };
}
